import dayjs from 'dayjs'
import * as React from 'react'

import GroupSessionService from '../services/GroupSessionService'

const GroupSessionContext = React.createContext()

/**
 * Provider component for group session management
 */
export function GroupSessionProvider({ children }) {
  const [upcomingGroupSessions, setUpcomingGroupSessions] = React.useState([])
  const [pastGroupSessions, setPastGroupSessions] = React.useState([])
  const [upcomingGroupSessionsLoaded, setUpcomingGroupSessionsLoaded] = React.useState(false)
  const [pastGroupSessionsLoaded, setPastGroupSessionsLoaded] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  const fetchUpComingGroupSessions = React.useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await GroupSessionService.getGroupSessions({
        dateAfter: dayjs().startOf('day').valueOf(),
      })
      setUpcomingGroupSessions(response)
      setUpcomingGroupSessionsLoaded(true)
    } catch (err) {
      console.error('Error fetching upcoming group sessions:', err)
      setError(err.message || 'Failed to fetch upcoming group sessions')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchPastGroupSessions = React.useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await GroupSessionService.getGroupSessions({
        dateBefore: dayjs().startOf('day').valueOf(),
      })
      setPastGroupSessions(response)
      setPastGroupSessionsLoaded(true)
    } catch (err) {
      console.error('Error fetching past group sessions:', err)
      setError(err.message || 'Failed to fetch past group sessions')
    } finally {
      setLoading(false)
    }
  }, [])

  const value = React.useMemo(
    () => ({
      upcomingGroupSessions,
      pastGroupSessions,
      upcomingGroupSessionsLoaded,
      pastGroupSessionsLoaded,
      loading,
      error,
      fetchUpComingGroupSessions,
      fetchPastGroupSessions,
    }),
    [upcomingGroupSessions, pastGroupSessions, upcomingGroupSessionsLoaded, pastGroupSessionsLoaded, loading, error, fetchUpComingGroupSessions, fetchPastGroupSessions],
  )

  return <GroupSessionContext.Provider value={value}>{children}</GroupSessionContext.Provider>
}

/**
 * Hook for using group session context
 */
export function useGroupSession() {
  const context = React.useContext(GroupSessionContext)
  if (!context) {
    throw new Error('useGroupSession must be used within a GroupSessionProvider')
  }
  return context
}
